import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, Modal, Select, message, List, Avatar, Flex } from 'antd';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import { getDocs, collection, query, where, doc, getDoc } from "firebase/firestore";
import { fireDB } from "../../funcs/firebase";
import CollabTeamWork from '../homev2/undraw_teamwork_zplp.svg';
import {
    InstagramOutlined,
    FacebookOutlined,
    CalendarOutlined,
    LinkOutlined,
    WhatsAppOutlined,
    PlayCircleFilled,
    RightOutlined,
    CheckCircleFilled,
    GiftOutlined,
    CrownOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    BarChartOutlined,
    AppleFilled,
    AndroidFilled,
    CopyOutlined
} from '@ant-design/icons';
import Sider from '../../comps/sider/main';
import callAPI from '../../comps/fetch/main';
import ShowPlans from '../../comps/show_plans/main';
import WhatsAppBanner from '../../comps/whatsappBanner/main';
import WhatsNew from '../../comps/whatsNew/main';

import './main.css';

const { Title, Text } = Typography;

function WelcomeOnboardingPage() {
    const navigate = useNavigate();

    const [userName, setUserName] = useState('');




    // Greetings in different languages
    const greetings = [
        'Vanakkam',      // Tamil
        'Namaste',       // Hindi
        'Hello',         // English
        'Hola',          // Spanish
        'Bonjour',       // French
        'Ciao',          // Italian
        'Hallo',         // German
        'Olá',           // Portuguese
        'Konnichiwa',    // Japanese
        'Annyeong',      // Korean
        'Merhaba',       // Turkish
        'Halo',          // Indonesian
    ];
    const [greeting] = useState(() => greetings[Math.floor(Math.random() * greetings.length)]);

    // Trial state
    const [userPlan, setUserPlan] = useState(null);
    const [userPlanLoading, setUserPlanLoading] = useState(true);
    const [trialDaysLeft, setTrialDaysLeft] = useState(7);
    const [isOnTrial, setIsOnTrial] = useState(true);
    const [isFreePlan, setIsFreePlan] = useState(true);
    const [daysSinceCreation, setDaysSinceCreation] = useState(0);

    // Plan modal state
    const [showPlanModal, setShowPlanModal] = useState(false);

    // Collab Invite States
    const [pendingInvites, setPendingInvites] = useState([]);
    const [showCollabModal, setShowCollabModal] = useState(false);

    // Shared Template Prompt State
    const [pendingSharedTemplate, setPendingSharedTemplate] = useState(null);

    // Features display state
    const [showAllFeatures, setShowAllFeatures] = useState(false);

    useEffect(() => {
        // Check if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        // Get user name from localStorage
        try {
            const userData = JSON.parse(localStorage.getItem('userData'));
            if (userData?.name) {
                setUserName(userData.name.split(' ')[0]); // Get first name
            } else if (userData?.username) {
                setUserName(userData.username);
            }
        } catch (e) {
            console.log('Error parsing user data');
        }

        // Fetch user plan data
        callAPI({ url: 'user_plan', method: 'GET', loadingState: setUserPlanLoading, dataState: setUserPlan });
    }, [navigate]);

    // Calculate trial days when user plan data is loaded
    useEffect(() => {
        if (!userPlan?.data) return;

        const planData = userPlan.data;
        const currentPlanId = planData.plan_details?.id;

        // Check if user is on free plan
        setIsFreePlan(currentPlanId === 'free');

        // Calculate days since account creation with error handling
        let calculatedDaysSinceCreation = 60; // Default to 60 days (no trial) on error

        try {
            if (planData.account_created) {
                const accountCreated = new Date(planData.account_created);

                // Check if date is valid
                if (!isNaN(accountCreated.getTime())) {
                    const now = new Date();
                    const differenceInTime = now.getTime() - accountCreated.getTime();
                    calculatedDaysSinceCreation = Math.floor(differenceInTime / (1000 * 3600 * 24));

                    // Sanity check: if negative or unreasonably large, default to 60
                    if (calculatedDaysSinceCreation < 0 || calculatedDaysSinceCreation > 3650) {
                        calculatedDaysSinceCreation = 60;
                    }
                }
            }
        } catch (error) {
            console.error('Error calculating days since creation:', error);
            calculatedDaysSinceCreation = 60; // Default to no trial on error
        }

        setDaysSinceCreation(calculatedDaysSinceCreation);

        // Check if still in trial period (less than 8 days old)
        if (calculatedDaysSinceCreation < 8 && currentPlanId === 'free') {
            setIsOnTrial(true);
            setTrialDaysLeft(7 - calculatedDaysSinceCreation);

            // Set the temporary plan ID for trial users
            localStorage.setItem('og_planID', currentPlanId);
            localStorage.setItem('planid', '428337'); // Pro plan ID for trial
        } else {
            setIsOnTrial(false);
            setTrialDaysLeft(0);
        }
    }, [userPlan]);

    // Fetch Pending Collab Invites
    useEffect(() => {
        const fetchPendingInvites = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;
            let username = "";
            try {
                const decoded = jwtDecode(token);
                username = decoded.default_page?.username || decoded.username;
            } catch (e) {
                console.error("Token decode error:", e);
                return;
            }

            if (!username) return;

            try {
                const q = query(
                    collection(fireDB, "collab_invitations"),
                    where("owner_username", "==", username),
                    where("status", "==", "pending")
                );
                const querySnapshot = await getDocs(q);
                const invites = [];
                querySnapshot.forEach((doc) => {
                    invites.push({ id: doc.id, ...doc.data() });
                });
                setPendingInvites(invites);
            } catch (err) {
                console.error("Error fetching invites:", err);
            }
        };
        fetchPendingInvites();

        // Check for pending shared template
        const checkPendingSharedTemplate = async () => {
            const pendingId = localStorage.getItem("pending_shared_template_id");
            if (pendingId) {
                try {
                    const docRef = doc(fireDB, "shared_templates", pendingId);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        setPendingSharedTemplate({ id: docSnap.id, ...docSnap.data() });
                    } else {
                        // Invalid ID, clear it
                        localStorage.removeItem("pending_shared_template_id");
                        localStorage.removeItem("show_pending_shared_template");
                    }
                } catch (error) {
                    console.error("Error fetching pending shared template:", error);
                }
            }
        };
        checkPendingSharedTemplate();
    }, []);

    // Auto-show modal when invites are fetched
    useEffect(() => {
        if (pendingInvites.length > 0 && !showCollabModal) {
            setShowCollabModal(true);
        }
    }, [pendingInvites]);

    const tutorials = [
        {
            id: 1,
            title: 'Automate Instagram Comments',
            description: 'Learn how to set up comment to DM automation for your posts',
            gradient: 'linear-gradient(135deg, #E1306C 0%, #833AB4 100%)',
            videoId: '5cdU1e1NAT0',
            duration: '5:00'
        },
        {
            id: 2,
            title: 'Global Automation',
            description: 'Automate all your posts and reels at once',
            gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            videoId: 'Fi4NKgzC19w',
            duration: '2:00'
        },
        {
            id: 3,
            title: 'Facebook Automation',
            description: 'Set up Facebook comment to Messenger automation',
            gradient: 'linear-gradient(135deg, #1877F2 0%, #0D47A1 100%)',
            videoId: '4HE7B23NClY',
            duration: '2:10'
        },
        {
            id: 4,
            title: 'Schedule Instagram Posts with AutoDM',
            description: 'Plan and schedule your content with automation',
            gradient: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
            videoId: '_28lJKsayLY',
            duration: '6:00'
        },
    ];

    const features = [
        {
            key: 'instagram',
            icon: <InstagramOutlined />,
            title: 'Instagram Automation',
            description: 'Comment to DM automation for Instagram posts & reels',
            gradient: '#E1306C', // Instagram pink
            path: '/ig'
        },
        {
            key: 'facebook',
            icon: <FacebookOutlined />,
            title: 'Facebook Automation',
            description: 'Automate Facebook page comments to Messenger',
            gradient: '#1877F2', // Facebook blue
            path: '/fb'
        },
        {
            key: 'later',
            icon: <CalendarOutlined />,
            title: 'Schedule Posts with AutoDM',
            description: 'Plan and schedule your content with automation',
            gradient: '#7C3AED', // Purple
            path: '/later',
        },
        {
            key: 'dashboard',
            icon: <BarChartOutlined />,
            title: 'Dashboard',
            description: 'View your automation stats and analytics',
            gradient: '#10B981', // Green
            path: '/dashboard'
        },
        {
            key: 'oia',
            icon: <LinkOutlined />,
            title: 'Open In App',
            description: 'Smart links that open directly in apps',
            gradient: '#0EA5E9', // Sky blue
            path: '/oia'
        },
        {
            key: 'referral',
            icon: <DollarOutlined />,
            title: 'Referral Dashboard',
            description: 'Earn up to 50% commission for every referral',
            gradient: '#F59E0B', // Amber
            path: '/referral-dashboard'
        }
    ];

    const [paymentLinkData, setPaymentLinkData] = useState()
    const [paymentLinkLoading, setPaymentLinkLoading] = useState()
    const [showINR, setShowINR] = useState(false)
    const [showWaitingPaymentUpdate, setShowWaitingPaymentUpdate] = useState(false)
    const [showWaitingPaymentFailed, setShowWaitingPaymentFailed] = useState(false)
    const [messageApi, contextHolder] = message.useMessage();

    // Determine currency based on timezone
    useEffect(() => {
        const user_tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setShowINR(user_tz === "Asia/Kolkata" || user_tz === "Asia/Calcutta");
    }, []);

    // Payment processing logic
    useEffect(() => {
        if (paymentLinkData?.open_url) {
            window.location.href = paymentLinkData?.payment_url
            return
        }

        if (paymentLinkData?.payment_url == undefined) {
            return
        }

        messageApi.destroy("payment-process")

        if (!showINR && window.LemonSqueezy) {
            const checkoutUrl = paymentLinkData.payment_url
            window.LemonSqueezy.Url.Open(checkoutUrl);
        }

        if (showINR && window.Razorpay) {
            try {
                const options = {
                    key: "rzp_live_JtjGCHjkESNzIZ",
                    name: "InstantDM",
                    description: "Affordable Comment to DM automation tool",
                    subscription_id: paymentLinkData.rzpy_sub_id,
                    handler: function (response) {
                        setShowPlanModal(false)
                        setShowWaitingPaymentUpdate(true)

                        message.success("Payment completed, Please wait while we're verfiying!")
                        let user_detail = JSON.parse(localStorage.getItem("userData"))
                        let igid = localStorage.getItem("ig_id")

                        const BOT_TOKEN = "7109212238:AAEsaFubA66rs0Ib6rgn5K2amKO9rnOkUv0";
                        const CHAT_ID = '776956029';
                        const url = `https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`;
                        let tele_message = `[SUBSCRIBED PLAN] - user ${user_detail?.emailid}  - Subscribed for  , IG ID = ${igid} , user id = ${user_detail?.user_id}`
                        let data = {
                            chat_id: CHAT_ID,
                            text: tele_message
                        };

                        fetch(url, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(data)
                        })
                            .then(response => response.json())
                            .then(data => {
                                console.log('Message sent successfully:', data);
                            })
                            .catch(error => {
                                console.error('Error sending message:', error);
                            });
                        setPaymentLinkData({})
                    },
                    error: function (response) {
                        setShowPlanModal(false)
                        setShowWaitingPaymentFailed(true)
                        setPaymentLinkData({})
                    },
                    prefill: {
                        name: paymentLinkData.customer_name,
                        email: paymentLinkData.customer_email,
                    },
                    "notes": paymentLinkData.notes != undefined ? { ...paymentLinkData.notes } : {},
                    theme: {
                        color: "#1677ff",
                    },
                    display: {
                        handler: function () {
                            return {
                                "offers": [
                                    {
                                        "type": "discount",
                                        "description": "10% discount on first purchase",
                                        "amount": 100
                                    },
                                    {
                                        "type": "coupon",
                                        "description": "Rs. 100 off on total bill",
                                        "code": "INSTANT10"
                                    }
                                ]
                            }
                        }
                    },
                };

                const razorpayInstance = new window.Razorpay(options);
                razorpayInstance.open();

            } catch (error) {
                alert("Error loading Razorpay: " + error.message);
            }
        } else if (!showINR && !window.LemonSqueezy) {
            // Fallback if LemonSqueezy is not loaded although it should be
            // message.error("Payment provider not loaded. Please refresh.");
        }
    }, [paymentLinkData, showINR, messageApi]);

    const handleFeatureClick = (feature) => {
        navigate(feature.path);
    };

    const openWhatsApp = () => {
        window.open('https://wa.me/919876543210?text=Hi, I need help setting up InstantDM', '_blank');
    };

    return (
        <div className="page-layout">
            {contextHolder}
            <Sider />
            <div className="page-content welcome-home-page">


            <div className="welcome-header">
                    <div className="welcome-header-content">
                        <Title level={2} className="welcome-title">
                            {userName ? `${userName}` : ''}! 👋
                        </Title>
                        <Text className="welcome-subtitle">
                            Facebook + Instagram Comment, stories, DM Automation
                        </Text>
                    </div>
                </div>
                {/* Free Trial Banner - Only show for free plan users */}
                {isFreePlan && (
                    <div className={`free-trial-banner ${!isOnTrial ? 'trial-expired' : ''}`}>
                        <div className="trial-icon">
                            {isOnTrial ? <GiftOutlined /> : <ClockCircleOutlined />}
                        </div>
                        <div className="trial-content">
                            <div className="trial-badge">
                                <CrownOutlined /> {isOnTrial ? 'PRO PLAN TRIAL' : 'FREE PLAN'}
                            </div>
                            {isOnTrial ? (
                                <>
                                    <h3>{trialDaysLeft > 0 ? `${trialDaysLeft} days left` : 'Last day'} of your Pro trial</h3>
                                    <p>Enjoying premium features no credit card required. {trialDaysLeft > 0 ? `Reverts to Free plan in ${trialDaysLeft} days.` : 'Reverts to Free plan tomorrow.'}</p>
                                </>
                            ) : (
                                <>
                                    <h3>Your free trial has ended</h3>
                                    <p>Upgrade to Pro for unlimited automations and premium features.</p>
                                </>
                            )}
                        </div>
                        <div className="trial-actions">
                            <Button type="default" className="upgrade-btn" onClick={() => setShowPlanModal(true)}>
                                {!isOnTrial ? 'Upgrade Now' : 'Show Plans'}
                            </Button>
                        </div>
                    </div>
                )}


  {/* Reusable Sections */}
                {(() => {
                    // Video Tutorials Section
                    const VideoTutorialsSection = (
                        <div className="welcome-section" key="tutorials" style={{ marginTop: '32px' }}>
                            <div className="section-header">
                                <Title level={3} className="section-title">
                                    Tutorial - Learn How to Use InstantDM
                                </Title>
                                <Text className="section-subtitle">
                                    Watch these quick tutorials to get started
                                </Text>
                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                gap: '20px',
                                marginTop: '20px'
                            }}>
                                {tutorials.map((tutorial) => (
                                    <div
                                        key={tutorial.id}
                                        style={{
                                            background: 'white',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                            border: '1px solid #f0f0f0'
                                        }}
                                    >
                                        {/* Thumbnail with Play Button */}
                                        <div
                                            style={{
                                                width: '100%',
                                                aspectRatio: '16/9',
                                                background: '#000',
                                                position: 'relative',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => window.open(`https://www.youtube.com/watch?v=${tutorial.videoId}`, '_blank')}
                                        >
                                            <img
                                                src={`https://img.youtube.com/vi/${tutorial.videoId}/maxresdefault.jpg`}
                                                alt={tutorial.title}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                                onError={(e) => {
                                                    // Fallback to hqdefault if maxresdefault doesn't exist
                                                    e.target.src = `https://img.youtube.com/vi/${tutorial.videoId}/hqdefault.jpg`;
                                                }}
                                            />
                                            {/* Play Button Overlay */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                width: '40px',
                                                height: '40px',
                                                background: 'rgba(255,0,0,0.9)',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                            }}>
                                                <PlayCircleFilled style={{ fontSize: '22px', color: 'white', marginLeft: '2px' }} />
                                            </div>
                                        </div>
                                        {/* Tutorial Info */}
                                        <div style={{ padding: '16px' }}>
                                            <h4 style={{
                                                margin: '0 0 8px 0',
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                color: '#1a1a1a'
                                            }}>
                                                {tutorial.title}
                                            </h4>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '13px',
                                                color: '#666',
                                                lineHeight: 1.4
                                            }}>
                                                {tutorial.description}
                                            </p>
                                            <div style={{
                                                marginTop: '12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                fontSize: '12px',
                                                color: '#999'
                                            }}>
                                                <PlayCircleFilled style={{ color: '#E1306C' }} />
                                                <span>{tutorial.duration}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );

                    // Features Section Component
                    const FeaturesSection = (
                        <div className="welcome-section" key="features">
                            {pendingInvites.length > 0 && (
                                <div className="collab-request-banner" onClick={() => setShowCollabModal(true)}>
                                    <div className="collab-banner-content">
                                        <img src={CollabTeamWork} alt="Collab" style={{ height: 60 }} />
                                        <div className="collab-banner-text">
                                            <h4>Collaboration requests pending</h4>
                                            <p>Review and automate posts with your Brands and Collaboration.</p>
                                        </div>
                                    </div>
                                    <Button type="primary" className="view-now-btn">
                                        View Now
                                    </Button>
                                </div>
                            )}

                            <div className="features-grid">
                                {(showAllFeatures ? features : features.slice(0, 3)).map((feature) => (
                                    <div
                                        key={feature.key}
                                        className="feature-card"
                                        onClick={() => handleFeatureClick(feature)}
                                    >
                                        <div
                                            className="feature-icon"
                                            style={{ background: feature.gradient }}
                                        >
                                            {feature.icon}
                                        </div>
                                        <div className="feature-content">
                                            <div className="feature-title-row">
                                                <h4>{feature.title}</h4>
                                                {feature.badge && (
                                                    <span className="feature-badge">{feature.badge}</span>
                                                )}
                                            </div>
                                            <p>{feature.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {!showAllFeatures && features.length > 3 && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <Button 
                                        type="default" 
                                        onClick={() => setShowAllFeatures(true)}
                                        icon={<RightOutlined />}
                                        style={{ 
                                            borderRadius: '8px',
                                            padding: '0 24px',
                                            height: '40px'
                                        }}
                                    >
                                        View All Features
                                    </Button>
                                </div>
                            )}
                        </div>
                    );

                    // Mobile App Section
                    const MobileAppSection = (
                        <div className="welcome-home-page" key="mobile-app" style={{ padding: 0 }}>
                            <div className="mobile-app-banner">
                                <div className="mobile-app-content">
                                    <h3>Download InstantDM App</h3>
                                    <p>Manage your automations, reply to DMs, and track analytics on the go. Available for iOS and Android.</p>
                                </div>
                                <div className="mobile-app-actions">
                                    <Button
                                        href="https://apps.apple.com/in/app/insta-dm-app-instantdm/id6756658913"
                                        target="_blank"
                                        icon={<AppleFilled style={{ fontSize: 18 }} />}
                                        className="app-store-btn"
                                    >
                                        App Store
                                    </Button>
                                    <Button
                                        href="https://play.google.com/store/apps/details?id=com.replbetter.instantdm"
                                        target="_blank"
                                        icon={<AndroidFilled style={{ fontSize: 18, color: '#3DDC84' }} />}
                                        className="app-store-btn"
                                        style={{ background: '#222 !important', color: 'white !important', border: '1px solid #333 !important' }}
                                    >
                                        Play Store
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );

                    // Shared Template Banner
                    const SharedTemplateBanner = pendingSharedTemplate ? (
                        <div className="welcome-section" key="shared-template" style={{ marginTop: 24 }}>
                            <div className="collab-request-banner" style={{
                                background: '#ffffff',
                                borderLeft: '4px solid #1890ff',
                                cursor: 'default',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
                            }}>
                                <div className="collab-banner-content">
                                    <div style={{
                                        background: '#e6f7ff',
                                        padding: 12,
                                        borderRadius: 12, // slightly rounded square looks more modern than circle often
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <GiftOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                                    </div>
                                    <div className="collab-banner-text">
                                        <h4 style={{ color: '#1a1a2e', fontSize: 16, marginBottom: 4 }}>Pending Shared Automation</h4>
                                        <p style={{ color: '#666', fontSize: 14, margin: 0 }}>
                                            You have a shared automation <strong>"{pendingSharedTemplate.name}"</strong> waiting to be copied.
                                        </p>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <Button
                                        type="text"
                                        onClick={() => {
                                            localStorage.removeItem("pending_shared_template_id");
                                            localStorage.removeItem("show_pending_shared_template");
                                            setPendingSharedTemplate(null);
                                        }}
                                        style={{ color: '#888' }}
                                    >
                                        Dismiss
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<CopyOutlined />}
                                        style={{ background: '#1890ff', borderRadius: 8, height: 38, padding: '0 20px' }}
                                        onClick={() => {
                                            // Clear pending state and redirect
                                            localStorage.removeItem("pending_shared_template_id");
                                            localStorage.removeItem("show_pending_shared_template");
                                            navigate(`/flow?shared_id=${pendingSharedTemplate.id}`);
                                        }}
                                    >
                                        Copy Now
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : null;

                    return [SharedTemplateBanner, FeaturesSection,VideoTutorialsSection, MobileAppSection, <WhatsNew key="whatsnew" setShowPlanModal={setShowPlanModal} />, ];
                })()}
                {/* Header Section */}
                



                
              

                <WhatsAppBanner />
            </div>


            {/* Plan Modal */}
            <Modal
                title="Plans"
                open={showPlanModal}
                onCancel={() => setShowPlanModal(false)}
                footer={null}
                width='100%'
            >
                <ShowPlans
                    currentPlan={localStorage.getItem("planid")}
                    messageApi={messageApi}
                    setPaymentLinkLoading={setPaymentLinkLoading}
                    setPaymentLinkData={setPaymentLinkData}
                />
            </Modal>

            {/* Collab Invitations Modal */}
            <Modal
                open={showCollabModal && pendingInvites?.length > 0}
                footer={null}
                onCancel={() => setShowCollabModal(false)}
                centered
                title="Pending Collaboration Requests"
            >
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <img src={CollabTeamWork} alt="Collaboration" style={{ height: 120, marginBottom: 16 }} />
                    <p style={{ color: "#666" }}>You have pending requests to automate posts. Review them to start collaborating.</p>
                </div>

                <List
                    itemLayout="horizontal"
                    dataSource={pendingInvites}
                    renderItem={(item) => (
                        <List.Item
                            actions={[<Button type="primary" onClick={() => {
                                window.location.href = `/manage-post/${item.post.id}?invite_id=${item.id}&action=review`;
                            }}>Review</Button>]}
                        >
                            <List.Item.Meta
                                avatar={<Avatar src={item.sender?.picture} />}
                                title={<a href="#">{item.sender?.username}</a>}
                                description={
                                    <div>
                                        <p style={{ margin: 0, fontSize: 12 }}>Requested to automate post</p>
                                        <Flex gap={5} style={{ marginTop: 5 }} align="center">
                                            <img src={item.post?.image} style={{ width: 30, height: 30, borderRadius: 4, objectFit: "cover" }} />
                                            <p style={{ margin: 0, fontSize: 10, color: "#999", maxWidth: 150, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.post?.caption}</p>
                                        </Flex>
                                    </div>
                                }
                            />
                        </List.Item>
                    )}

                />
            </Modal>
        </div>
    );
}

export default WelcomeOnboardingPage;
